"use client";
import {
  Button,
  CurrencyInput,
  DateOfBirthPicker,
  FileInputProfile,
  Input,
  MultiSelectBadges,
  PhoneInput,
  ScreenLoader,
  SelectInput,
  SelectSearchInput,
  TextArea,
} from "@/components";
import { useFormStepOne } from "@/hooks";
import { useProfileSetup } from "@/hooks/react-query";
import { OptionsJobber } from "@/types";
import React from "react";
import { FormProvider } from "react-hook-form";
import {
  FaGithub,
  FaGlobe,
  FaLinkedinIn,
  FaSquareBehance,
} from "react-icons/fa6";
import { useAuth } from "@/providers/supabase-auth-provider";
import { useTranslations } from "next-intl";

export interface FormStepOneProp {
  optionsJobber: {
    options?: OptionsJobber;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  setStep: (step: number) => void;
  userId: string;
  buttonText?: string;
}

const FormStepOne: React.FC<FormStepOneProp> = ({
  optionsJobber,
  setStep,
  userId,
  buttonText,
}) => {
  const {
    methods,
    countries,
    countryList,
    isLoadingCountry,
    setCountrySearchQuery,
    hasMoreCountries,
    fetchNextCountriesPage,
    setPhoneCountrySearchQuery,
    hasMorePhoneCountries,
    fetchNextPhoneCountriesPage,
    isFetchingNextPhoneCountriesPage,
    statesList,
    isLoadingState,
    onSubmit,
  } = useFormStepOne(setStep, userId);

  const { user } = useAuth();
  const t = useTranslations("jobberProfile.forms.stepOne");

  React.useEffect(() => {
    const metaPhone = (user?.user_metadata as any)?.phone;
    if (!metaPhone || typeof metaPhone !== "object") return;
    const current = methods.getValues("phone_number");
    const alreadyFilled = Boolean(current?.phone_number) || Boolean(current?.country_id);
    if (alreadyFilled) return;
    methods.setValue(
      "phone_number",
      {
        country_id: typeof metaPhone.country_id === "string" ? metaPhone.country_id : "",
        country_phone_code:
          typeof metaPhone.country_phone_code === "string" ? metaPhone.country_phone_code : "",
        phone_number: typeof metaPhone.phone_number === "string" ? metaPhone.phone_number : "",
      } as any,
      { shouldDirty: false, shouldValidate: false }
    );
  }, [user, methods]);

  const { data: restoredData } = useProfileSetup(userId as string);

  // Fallback hydration: if form still empty after first render and restoredData present, inject it directly.
  React.useEffect(() => {
    if (!restoredData?.general_information) return;
    const gi = restoredData.general_information as any;
    // Detect empty form (no first_name & no last_names & no location_country.id)
    const current = methods.getValues();
    const isEmpty = !current.first_name && !current.last_names && !current.location_country?.id;
    if (isEmpty) {
      const metaPhone = (user?.user_metadata as any)?.phone;
      const hasMetaPhone = metaPhone && typeof metaPhone === "object";
      const mergedPhone = (() => {
        const fromApi = gi.phone_number || {};
        const phone_number = fromApi.phone_number || (hasMetaPhone ? metaPhone.phone_number : "");
        const country_id = fromApi.country_id || (hasMetaPhone ? metaPhone.country_id : "");
        const country_phone_code = fromApi.country_phone_code || (hasMetaPhone ? metaPhone.country_phone_code : "");
        return { country_id, country_phone_code, phone_number };
      })();

      const mapped = {
        first_name: gi.first_name || "",
        last_names: gi.last_names || "",
        location_country: gi.location_country || { id: "", name: "" },
        location_state: gi.location_state || { id: "", name: "" },
        birth_date: gi.birth_date || null,
        phone_number: mergedPhone,
        monthly_expect_full_time: gi.monthly_expect_full_time || 0,
        monthly_expect_part_time: gi.monthly_expect_part_time || 0,
        rate_per_hour: gi.rate_per_hour || 0,
        linkedin_url: gi.linkedin_url || "",
        behance_url: gi.behance_url || "",
        website_url: gi.website_url || "",
        github_url: gi.github_url || "",
        profile_picture: null,
        cover_letter: gi.cover_letter || "",
        open_to_work: gi.open_to_work === true
          ? { id: "true", name: "Yes" }
          : gi.open_to_work === false
            ? { id: "false", name: "No" }
            : { id: "", name: "" },
        work_modes: Array.isArray(gi.work_modes) ? gi.work_modes : [],
      } as any;
      methods.reset(mapped);
      // Also sync store so completion checks work
      try {
        if ((window as any).jobbiDebug) {

        }
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredData?.general_information, user]);

  return (
    <FormProvider {...methods}>
      <form className="pb-3" onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="md:p-8">
          {methods.formState.isSubmitting && <ScreenLoader />}

          {/* Mobile Layout - Keep exactly as is */}
          <div className="block lg:hidden">
            {/* Profile Picture - Top section for mobile */}
            <div className="flex justify-center items-center mb-6 pt-4">
              <FileInputProfile
                name="profile_picture"
                fallbackSrc={
                  restoredData?.general_information?.picture_url as
                  | string
                  | undefined
                }
                circleSize="w-24 h-24 sm:w-28 sm:h-28"
              />
            </div>

            {/* Dynamic Name Display */}
            <div className="mb-6">
              <h2 className="font-semibold text-xl sm:text-2xl text-center">
                {methods.watch("first_name") || t("yourName")}{" "}
                {methods.watch("last_names")}
              </h2>
            </div>

            {/* Profile Information */}
            <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
              <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
                  <path d="M11.0997 12.6016L11.0999 10.3518C11.1 9.10905 10.0926 8.10156 8.8499 8.10156H2.85035C1.60781 8.10156 0.60049 9.10877 0.600351 10.3513L0.600098 12.6016M8.1001 2.85156C8.1001 4.0942 7.09274 5.10156 5.8501 5.10156C4.60746 5.10156 3.6001 4.0942 3.6001 2.85156C3.6001 1.60892 4.60746 0.601562 5.8501 0.601562C7.09274 0.601562 8.1001 1.60892 8.1001 2.85156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t("profileInformation")}
              </h3>

            {/* Personal Information Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                name="first_name"
                label={t("firstName")}
                className="w-full bg-[#F9F9F9]"
                placeholder={t("firstNamePlaceholder")}
                required
                noBorder
                noShadow
              />

              <Input
                name="last_names"
                label={t("lastName")}
                className="w-full bg-[#F9F9F9]"
                placeholder={t("lastNamePlaceholder")}
                required
                noBorder
                noShadow
              />

              <SelectSearchInput
                name="location_country"
                label={t("locationCountry")}
                placeholder={t("locationCountryPlaceholder")}
                className={`${countryList.length === 0
                  ? "opacity-50 pointer-events-none select-none"
                  : "bg-[#F9F9F9]"
                  }`}
                required
                items={countryList}
                loading={isLoadingCountry}
                onSearch={setCountrySearchQuery}
                hasMore={hasMoreCountries}
                onLoadMore={() => hasMoreCountries && fetchNextCountriesPage()}
                onResetPage={() => setCountrySearchQuery("")}
                classNameContainer="w-full z-100"
                noBorder
                noShadow
              />

              <SelectSearchInput
                name="location_state"
                placeholder={t("locationStatePlaceholder")}
                label={t("locationState")}
                items={statesList}
                loading={isLoadingState}
                required
                classNameContainer="w-full z-100"
                className="bg-[#F9F9F9]"
                noBorder
                noShadow
              />

              <DateOfBirthPicker name="birth_date" label={t("birthdate")} noBorder noShadow className="bg-[#F9F9F9]" />

              <PhoneInput
                name="phone_number"
                countries={countries}
                defaultCountryId={methods.watch("location_country")?.id}
                label={t("phoneNumber")}
                placeholder={t("phoneNumberPlaceholder")}
                required
                isLoading={isLoadingCountry}
                onSearchChange={setPhoneCountrySearchQuery}
                hasNextPage={hasMorePhoneCountries}
                fetchNextPage={fetchNextPhoneCountriesPage}
                isFetchingNextPage={isFetchingNextPhoneCountriesPage}
                noBorder
                noShadow
                className="bg-[#F9F9F9]"
              />
            </div>
            </div>

             {/* Divider */}
            <div className="border-t border-secondary-100 my-6"></div>

            {/* Cover Letter */}
            <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
              <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9.12671 12.6016V9.75946C9.12671 9.23624 9.55087 8.81209 10.0741 8.81209H12.9162M3.91618 7.23314H5.81092M3.91618 4.39103H9.6004M11.4951 11.1805C11.0735 11.5578 10.636 12.0052 10.3598 12.2958C10.176 12.4892 9.922 12.6016 9.65521 12.6016H2.49491C1.44848 12.6016 0.600177 11.7533 0.60017 10.7068L0.600098 2.49635C0.600091 1.44991 1.44839 0.6016 2.49483 0.601596L11.0212 0.601563C12.0676 0.601558 12.9159 1.44983 12.9159 2.49625L12.9162 9.38222C12.9162 9.62446 12.8238 9.85732 12.6556 10.0317C12.3448 10.3542 11.8256 10.8848 11.4951 11.1805Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t("coverLetter")}
              </h3>
              <TextArea
                name="cover_letter"
                rows={6}
                maxLength={1000}
                placeholder={t("coverLetterPlaceholder")}
                noBorder
                noShadow
                className="bg-[#F9F9F9]"
              />
            </div>

            {/* Availability */}
            <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
              <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M6.9751 4.92156V6.88274C6.9751 7.01004 7.02567 7.13213 7.11569 7.22215L8.1751 8.28156M3.1351 10.9216L1.4551 12.6016M12.4951 12.6016L10.8151 10.9216M2.4751 0.601562L0.600098 2.47656M13.3501 2.47656L11.4751 0.601562M12.2551 7.08156C12.2551 9.99763 9.89116 12.3616 6.9751 12.3616C4.05904 12.3616 1.6951 9.99763 1.6951 7.08156C1.6951 4.1655 4.05904 1.80156 6.9751 1.80156C9.89116 1.80156 12.2551 4.1655 12.2551 7.08156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                {t("availability")}
              </h3>
              <SelectInput
                name="open_to_work"
                options={[
                  { id: "true", name: t("yes") },
                  { id: "false", name: t("no") },
                ]}
                placeholder={t("openToWork")}
                required
                noBorder
                noShadow
                className="bg-[#F9F9F9]"
              />
            </div>

            {/* Work Preferences */}
            <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
              <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M6.9751 4.92156V6.88274C6.9751 7.01004 7.02567 7.13213 7.11569 7.22215L8.1751 8.28156M3.1351 10.9216L1.4551 12.6016M12.4951 12.6016L10.8151 10.9216M2.4751 0.601562L0.600098 2.47656M13.3501 2.47656L11.4751 0.601562M12.2551 7.08156C12.2551 9.99763 9.89116 12.3616 6.9751 12.3616C4.05904 12.3616 1.6951 9.99763 1.6951 7.08156C1.6951 4.1655 4.05904 1.80156 6.9751 1.80156C9.89116 1.80156 12.2551 4.1655 12.2551 7.08156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round"/>
                </svg>
                {t("workPreferences")} <span className="text-red-500">*</span>
              </h3>
              <MultiSelectBadges
                name="work_modes"
                options={optionsJobber.options?.work_modes || []}
                placeholder={t("workPreferencesPlaceholder")}
                required
                noBorder
                noShadow
                className="bg-[#F9F9F9]"
              />
            </div>

            {/* Divider */}
            <div className="border-t border-secondary-100 my-6"></div>

            {/* Monthly Expectation Section - Half width entries */}
            <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
              <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                  <path d="M7.06677 3.9349C6.93407 3.55839 6.69173 3.23021 6.37094 2.99259C6.05016 2.75497 5.6656 2.61878 5.26676 2.60156H2.6001C2.06966 2.60156 1.56096 2.81228 1.18588 3.18735C0.810811 3.56242 0.600098 4.07113 0.600098 4.60156C0.600098 5.132 0.810811 5.6407 1.18588 6.01578C1.56096 6.39085 2.06966 6.60156 2.6001 6.60156H5.26676C5.7972 6.60156 6.30591 6.81228 6.68098 7.18735C7.05605 7.56242 7.26676 8.07113 7.26676 8.60156C7.26676 9.132 7.05605 9.6407 6.68098 10.0158C6.30591 10.3908 5.7972 10.6016 5.26676 10.6016H2.6001C2.20126 10.5843 1.8167 10.4482 1.49592 10.2105C1.17513 9.97292 0.932795 9.64474 0.800098 9.26823M3.93343 0.601562V2.60156M3.93343 10.6016V12.6016" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t("monthlyExpectation")}
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <CurrencyInput
                  label={t("fullTime")}
                  name="monthly_expect_full_time"
                  className="w-full bg-[#F9F9F9]"
                  placeholder="0.00"
                  required
                  noBorder
                  noShadow
                />
                <CurrencyInput
                  label={t("partTime")}
                  name="monthly_expect_part_time"
                  className="w-full bg-[#F9F9F9]"
                  placeholder="0.00"
                  required
                  noBorder
                  noShadow
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  label={t("ratePerHour")}
                  name="rate_per_hour"
                  className="w-full bg-[#F9F9F9]"
                  placeholder="0.00"
                  noBorder
                  noShadow
                />
                <div></div> {/* Empty div for grid alignment */}
              </div>
            </div>
          </div>

          {/* Desktop Layout - Card on left, form fields on right */}
          <div className="hidden lg:block">
            <div className="flex gap-6 mb-6">
              {/* Left Card: Profile Picture, Name, Cover Letter, Work Status */}
              <div className="w-90 flex-shrink-0">
                <div className="bg-white border border-[#EDEEFE] rounded-2xl p-6 space-y-6 sticky top-6">
                  <div className="flex justify-center">
                    <FileInputProfile
                      name="profile_picture"
                      fallbackSrc={
                        restoredData?.general_information?.picture_url as
                        | string
                        | undefined
                      }
                      circleSize="w-32 h-32"
                    />
                  </div>

                  {/* Dynamic Name Display */}
                  <div className="text-center">
                    <h2 className="font-semibold text-2xl">
                      {methods.watch("first_name") || t("yourName")}{" "}
                      {methods.watch("last_names")}
                    </h2>
                  </div>

                  <div className="border border-[#EDEEFE] rounded-2xl p-4">
                    <h3 className="font-normal text-sm mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M9.12671 12.6016V9.75946C9.12671 9.23624 9.55087 8.81209 10.0741 8.81209H12.9162M3.91618 7.23314H5.81092M3.91618 4.39103H9.6004M11.4951 11.1805C11.0735 11.5578 10.636 12.0052 10.3598 12.2958C10.176 12.4892 9.922 12.6016 9.65521 12.6016H2.49491C1.44848 12.6016 0.600177 11.7533 0.60017 10.7068L0.600098 2.49635C0.600091 1.44991 1.44839 0.6016 2.49483 0.601596L11.0212 0.601563C12.0676 0.601558 12.9159 1.44983 12.9159 2.49625L12.9162 9.38222C12.9162 9.62446 12.8238 9.85732 12.6556 10.0317C12.3448 10.3542 11.8256 10.8848 11.4951 11.1805Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t("coverLetter")}
                      </h3>

                    <TextArea
                      name="cover_letter"
                      rows={12}
                      maxLength={1000}
                      placeholder={t("coverLetterPlaceholder")}
                      noBorder
                      noShadow
                      className="bg-[#F9F9F9]"
                    />
                  </div>

                  <div className="border border-[#EDEEFE] rounded-2xl p-4">
                    <h3 className="font-normal text-sm mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M6.9751 4.92156V6.88274C6.9751 7.01004 7.02567 7.13213 7.11569 7.22215L8.1751 8.28156M3.1351 10.9216L1.4551 12.6016M12.4951 12.6016L10.8151 10.9216M2.4751 0.601562L0.600098 2.47656M13.3501 2.47656L11.4751 0.601562M12.2551 7.08156C12.2551 9.99763 9.89116 12.3616 6.9751 12.3616C4.05904 12.3616 1.6951 9.99763 1.6951 7.08156C1.6951 4.1655 4.05904 1.80156 6.9751 1.80156C9.89116 1.80156 12.2551 4.1655 12.2551 7.08156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round"/>
                        </svg>
                       {t("availability")}
                      </h3>

                    <SelectInput
                      name="open_to_work"
                      options={[
                        { id: "true", name: t("yes") },
                        { id: "false", name: t("no") },
                      ]}
                      placeholder={t("openToWork")}
                      required
                      noBorder
                      noShadow
                      className="bg-[#F9F9F9]"
                      
                    />
                  </div>

                  <div className="border border-[#EDEEFE] rounded-2xl p-4">
                    <h3 className="font-normal text-sm mb-4 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M6.9751 4.92156V6.88274C6.9751 7.01004 7.02567 7.13213 7.11569 7.22215L8.1751 8.28156M3.1351 10.9216L1.4551 12.6016M12.4951 12.6016L10.8151 10.9216M2.4751 0.601562L0.600098 2.47656M13.3501 2.47656L11.4751 0.601562M12.2551 7.08156C12.2551 9.99763 9.89116 12.3616 6.9751 12.3616C4.05904 12.3616 1.6951 9.99763 1.6951 7.08156C1.6951 4.1655 4.05904 1.80156 6.9751 1.80156C9.89116 1.80156 12.2551 4.1655 12.2551 7.08156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                      {t("workPreferences")} <span className="text-red-500">*</span>
                    </h3>

                  <MultiSelectBadges
                    name="work_modes"
                    options={optionsJobber.options?.work_modes || []}
                    placeholder={t("workPreferencesPlaceholder")}
                    required
                    noBorder
                    noShadow
                    className="bg-[#F9F9F9]"
                  />
                  </div>
                </div>
              </div>

              {/* Right Side: Two Column Grid for rest of fields */}
              <div className="flex-1 bg-[#FFF] border border-[#EDEEFE] rounded-2xl p-3">
                <div className="bg-white border border-[#EDEEFE] rounded-2xl p-6">
                  {/* Profile Information Section Title */}
                  <div className="mb-4">
                    <h3 className="font-normal text-sm mb-4 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
                        <path d="M11.0997 12.6016L11.0999 10.3518C11.1 9.10905 10.0926 8.10156 8.8499 8.10156H2.85035C1.60781 8.10156 0.60049 9.10877 0.600351 10.3513L0.600098 12.6016M8.1001 2.85156C8.1001 4.0942 7.09274 5.10156 5.8501 5.10156C4.60746 5.10156 3.6001 4.0942 3.6001 2.85156C3.6001 1.60892 4.60746 0.601562 5.8501 0.601562C7.09274 0.601562 8.1001 1.60892 8.1001 2.85156Z" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {t("profileInformation")}
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    
                    {/* Column 1: First Name, Country, Phone Number */}
                    <div className="space-y-6">
                    <Input
                      name="first_name"
                      label={t("firstName")}
                      className="w-full bg-[#F9F9F9]"
                      placeholder={t("firstNamePlaceholder")}
                      required
                      noBorder
                      noShadow
                      
                    />

                    <SelectSearchInput
                      name="location_country"
                      label={t("locationCountry")}
                      placeholder={t("locationCountryPlaceholder")}
                      className={`${countryList.length === 0
                        ? "opacity-50 pointer-events-none select-none"
                        : " bg-[#F9F9F9]"
                        }`}
                      required
                      items={countryList}
                      loading={isLoadingCountry}
                      onSearch={setCountrySearchQuery}
                      hasMore={hasMoreCountries}
                      onLoadMore={() => hasMoreCountries && fetchNextCountriesPage()}
                      onResetPage={() => setCountrySearchQuery("")}
                      classNameContainer="w-full z-100 "
                      noBorder
                      noShadow
                    />

                    <PhoneInput
                      name="phone_number"
                      countries={countries}
                      defaultCountryId={methods.watch("location_country")?.id}
                      label={t("phoneNumber")}
                      placeholder={t("phoneNumberPlaceholder")}
                      required
                      isLoading={isLoadingCountry}
                      onSearchChange={setPhoneCountrySearchQuery}
                      hasNextPage={hasMorePhoneCountries}
                      fetchNextPage={fetchNextPhoneCountriesPage}
                      isFetchingNextPage={isFetchingNextPhoneCountriesPage}
                      noBorder
                      noShadow
                      className="bg-[#F9F9F9]"
                    />
                  </div>

                  {/* Column 2: Last Name, Location State, Birthdate */}
                  <div className="space-y-6">
                    <Input
                      name="last_names"
                      label={t("lastName")}
                      className="w-full bg-[#F9F9F9]"
                      placeholder={t("lastNamePlaceholder")}
                      required
                      noBorder
                      noShadow
                    />

                    <SelectSearchInput
                      name="location_state"
                      placeholder={t("locationStatePlaceholder")}
                      label={t("locationState")}
                      items={statesList}
                      loading={isLoadingState}
                      required 
                      className="bg-[#F9F9F9]"
                      classNameContainer="w-full z-100"
                      noBorder
                      noShadow
                    />

                    <DateOfBirthPicker 
                    name="birth_date" 
                    label={t("birthdate")} 
                    noBorder noShadow className="bg-[#F9F9F9]" />
                  </div>
                </div>
                </div>

                {/* Monthly Expectation Card */}
                <div className="bg-white border border-[#EDEEFE] rounded-2xl p-6 mt-6">
                  <h3 className="font-normal text-sm mb-4 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="14" viewBox="0 0 8 14" fill="none">
                      <path d="M7.06677 3.9349C6.93407 3.55839 6.69173 3.23021 6.37094 2.99259C6.05016 2.75497 5.6656 2.61878 5.26676 2.60156H2.6001C2.06966 2.60156 1.56096 2.81228 1.18588 3.18735C0.810811 3.56242 0.600098 4.07113 0.600098 4.60156C0.600098 5.132 0.810811 5.6407 1.18588 6.01578C1.56096 6.39085 2.06966 6.60156 2.6001 6.60156H5.26676C5.7972 6.60156 6.30591 6.81228 6.68098 7.18735C7.05605 7.56242 7.26676 8.07113 7.26676 8.60156C7.26676 9.132 7.05605 9.6407 6.68098 10.0158C6.30591 10.3908 5.7972 10.6016 5.26676 10.6016H2.6001C2.20126 10.5843 1.8167 10.4482 1.49592 10.2105C1.17513 9.97292 0.932795 9.64474 0.800098 9.26823M3.93343 0.601562V2.60156M3.93343 10.6016V12.6016" stroke="#151515" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t("monthlyExpectation")}
                  </h3>
                  <div className="grid grid-cols-3 gap-6">
                    <CurrencyInput
                      label={t("fullTime")}
                      name="monthly_expect_full_time"
                      className="w-full bg-[#F9F9F9]"
                      placeholder="0.00"
                      required
                      noBorder
                      noShadow
                    />

                    <CurrencyInput
                      label={t("partTime")}
                      name="monthly_expect_part_time"
                      className="w-full bg-[#F9F9F9]"
                      placeholder="0.00"
                      required
                      noBorder
                      noShadow
                    />

                    <CurrencyInput
                      label={t("ratePerHour")}
                      name="rate_per_hour"
                      className="w-full bg-[#F9F9F9]"
                      placeholder="0.00"
                      noBorder
                      noShadow
                    />
                  </div>
                </div>

                {/* Social Links Card */}
                <div className="bg-white border border-[#EDEEFE] rounded-2xl p-6 mt-6">
                  <h3 className="font-normal text-sm mb-4 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="14" viewBox="0 0 11 14" fill="none">
                      <path d="M5.29575 12.6016C5.29575 12.6016 9.9914 8.42765 9.9914 5.29721C9.9914 2.70388 7.88909 0.601562 5.29575 0.601562C2.70241 0.601562 0.600098 2.70388 0.600098 5.29721C0.600098 8.42765 5.29575 12.6016 5.29575 12.6016Z" stroke="#151515" strokeWidth="1.2"/>
                      <path d="M6.79594 5.10166C6.79594 5.93009 6.12437 6.60166 5.29594 6.60166C4.46752 6.60166 3.79594 5.93009 3.79594 5.10166C3.79594 4.27323 4.46752 3.60166 5.29594 3.60166C6.12437 3.60166 6.79594 4.27323 6.79594 5.10166Z" stroke="#151515" strokeWidth="1.2"/>
                    </svg>
                    {t("socials")}
                  </h3>
                  <div className="grid grid-cols-2 gap-6">
                    {/* Column 1: LinkedIn and Behance */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex justify-center items-center rounded-full bg-blue-500 text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                          <FaLinkedinIn />
                        </div>
                        <div className="flex-1">
                          <Input
                            name="linkedin_url"
                            label={t("linkedinProfile")}
                            placeholder={t("linkedinPlaceholder")}
                            className="w-full bg-[#F9F9F9]"
                            noBorder
                            noShadow
                          />
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="flex justify-center items-center rounded-full bg-brand-500 text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                          <FaSquareBehance />
                        </div>
                        <div className="flex-1">
                          <Input
                            name="behance_url"
                            label={t("behanceProfile")}
                            placeholder={t("behancePlaceholder")}
                            className="w-full bg-[#F9F9F9]"
                            noBorder
                            noShadow
                          />
                        </div>
                      </div>
                    </div>

                    {/* Column 2: Website and GitHub */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex justify-center items-center rounded-full bg-gray-500 text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                          <FaGlobe />
                        </div>
                        <div className="flex-1">
                          <Input
                            name="website_url"
                            label={t("personalWebsite")}
                            placeholder={t("websitePlaceholder")}
                            className="w-full bg-[#F9F9F9]"
                            noBorder
                            noShadow
                          />
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="flex justify-center items-center rounded-full bg-black text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                          <FaGithub />
                        </div>
                        <div className="flex-1">
                          <Input
                            name="github_url"
                            label={t("githubProfile")}
                            placeholder={t("githubPlaceholder")}
                            className="w-full bg-[#F9F9F9]"
                            noBorder
                            noShadow
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Links Section - Mobile */}
          <div className="lg:hidden">
             {/* Divider */}
            <div className="border-t border-secondary-100 my-6"></div>
            
            <div className="mb-6 bg-white border border-[#EDEEFE] rounded-2xl p-4">
              <h3 className="font-normal text-base sm:text-sm mb-4 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="14" viewBox="0 0 11 14" fill="none">
                  <path d="M5.29575 12.6016C5.29575 12.6016 9.9914 8.42765 9.9914 5.29721C9.9914 2.70388 7.88909 0.601562 5.29575 0.601562C2.70241 0.601562 0.600098 2.70388 0.600098 5.29721C0.600098 8.42765 5.29575 12.6016 5.29575 12.6016Z" stroke="#151515" strokeWidth="1.2"/>
                  <path d="M6.79594 5.10166C6.79594 5.93009 6.12437 6.60166 5.29594 6.60166C4.46752 6.60166 3.79594 5.93009 3.79594 5.10166C3.79594 4.27323 4.46752 3.60166 5.29594 3.60166C6.12437 3.60166 6.79594 4.27323 6.79594 5.10166Z" stroke="#151515" strokeWidth="1.2"/>
                </svg>
                {t("socials")}
              </h3>

              {/* Mobile Layout - Keep as vertical list */}
              <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex justify-center items-center rounded-full bg-blue-500 text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                  <FaLinkedinIn />
                </div>
                <div className="flex-1">
                  <Input
                    name="linkedin_url"
                    label={t("linkedinProfile")}
                    placeholder={t("linkedinPlaceholder")}
                    className="w-full bg-[#F9F9F9]"
                    noBorder
                    noShadow
                  />
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex justify-center items-center rounded-full bg-brand-500 text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                  <FaSquareBehance />
                </div>
                <div className="flex-1">
                  <Input
                    name="behance_url"
                    label={t("behanceProfile")}
                    placeholder={t("behancePlaceholder")}
                    className="w-full bg-[#F9F9F9]"
                    noBorder
                    noShadow
                  />
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex justify-center items-center rounded-full bg-gray-500 text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                  <FaGlobe />
                </div>
                <div className="flex-1">
                  <Input
                    name="website_url"
                    label={t("personalWebsite")}
                    placeholder={t("websitePlaceholder")}
                    className="w-full bg-[#F9F9F9]"
                    noBorder
                    noShadow
                  />
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex justify-center items-center rounded-full bg-black text-white w-[30px] h-[30px] text-base flex-shrink-0 mt-6">
                  <FaGithub />
                </div>
                <div className="flex-1">
                  <Input
                    name="github_url"
                    label={t("githubProfile")}
                    placeholder={t("githubPlaceholder")}
                    className="w-full bg-[#F9F9F9]"
                    noBorder
                    noShadow
                  />
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="mt-6 flex justify-center sm:justify-end">
          <Button
            type="button"
            rounded="rounded-xl"
            className="w-full sm:w-[208px]"
            isLoading={methods.formState.isSubmitting}
            onClick={async () => {
              const ok = await methods.trigger();
              if (ok) {
                // submit will call onSubmit which saves, updates store, and advances the step
                await methods.handleSubmit(onSubmit)();
              } else {
                // errors are shown by RHF; keep UX consistent
              }
            }}
          >
            {buttonText || t('next')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

FormStepOne.displayName = "FormStepOne";

export default FormStepOne;
